/**
 * الملاحة النهائية — خمسة عناصر ولا شيء غيرها (Design Freeze).
 * Sidebar فوق 900px · Bottom Navigation تحتها.
 */

import { NavLink } from 'react-router-dom';
import { color } from '@/design/tokens';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { useAuth } from '@/services/auth';

type IconProps = { size?: number };

const stroke = { stroke: 'currentColor', strokeWidth: 1.3, strokeLinecap: 'round' as const, fill: 'none' };

function IconHome({ size = 14 }: IconProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden="true">
      <path d="M2 6.2 7 2l5 4.2V12H2z" {...stroke} />
    </svg>
  );
}
function IconMap({ size = 14 }: IconProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden="true">
      <path d="M1.5 3.5 5 2l4 1.5L12.5 2v8.5L9 12 5 10.5 1.5 12z" {...stroke} />
      <path d="M5 2v8.5M9 3.5V12" {...stroke} />
    </svg>
  );
}
function IconOpportunity({ size = 14 }: IconProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden="true">
      <path d="M1.5 10.5 5 6l3 2.6L12.5 3" {...stroke} />
      <path d="M9.6 3h2.9v2.9" {...stroke} />
    </svg>
  );
}
function IconWatch({ size = 14 }: IconProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden="true">
      <circle cx="7" cy="7" r="4.6" {...stroke} />
      <circle cx="7" cy="7" r="1.4" {...stroke} />
    </svg>
  );
}
function IconReports({ size = 14 }: IconProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden="true">
      <path d="M3 1.8h5.2L11 4.6V12.2H3z" {...stroke} />
      <path d="M5 7h4M5 9.4h4" {...stroke} />
    </svg>
  );
}

export interface NavEntry {
  to: string;
  label: string;
  Icon: (p: IconProps) => JSX.Element;
}

export const NAV_ITEMS: NavEntry[] = [
  { to: '/', label: 'الرئيسية', Icon: IconHome },
  { to: '/map', label: 'الخريطة', Icon: IconMap },
  { to: '/opportunities', label: 'الفرص', Icon: IconOpportunity },
  { to: '/watchlist', label: 'المراقبة', Icon: IconWatch },
  { to: '/reports', label: 'التقارير', Icon: IconReports },
];

/**
 * علامة نطاق داخل التطبيق.
 *
 * ⛔ لا رسم للرمز هنا. النسخة السابقة كانت شعاراً مؤقتاً من صنعنا وقد أُزيلت
 *    بموجب Brand Design Freeze. الرمز يأتي من ملف الأصل الرسمي فقط.
 */
export function BrandMark(): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '4px 8px 16px' }}>
      <BrandLogo size={26} />
    </div>
  );
}

export function Sidebar(): JSX.Element {
  const { status, user } = useAuth();
  const authenticated = status === 'authenticated';
  const accountTo = authenticated ? '/account' : '/auth';
  const accountLabel = authenticated ? (user?.name || 'الحساب') : 'تسجيل الدخول';
  const accountHint = authenticated ? (user?.phone ?? '') : 'لحفظ المراقبة والتقارير';

  return (
    <nav className="nq-sidebar" aria-label="الملاحة الرئيسية">
      <BrandMark />
      {NAV_ITEMS.map(({ to, label, Icon }) => (
        <NavLink key={to} to={to} end={to === '/'} className="nq-navitem">
          <Icon size={15} />
          <span>{label}</span>
        </NavLink>
      ))}
      <div style={{ flex: 1 }} />
      <NavLink
        to={accountTo}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: 12,
          borderRadius: 12,
          background: color.surface700,
          border: `1px solid ${color.hairlineSoft}`,
          color: color.text,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'rgba(0,229,199,.14)',
            color: color.teal,
            display: 'grid',
            placeItems: 'center',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          ن
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 500 }}>{accountLabel}</div>
          <div style={{ fontSize: 10.5, color: color.micro }}>{accountHint}</div>
        </div>
      </NavLink>
    </nav>
  );
}

export function BottomNavigation(): JSX.Element {
  return (
    <nav className="nq-bottomnav" aria-label="الملاحة السفلية">
      {NAV_ITEMS.map(({ to, label, Icon }) => (
        <NavLink key={to} to={to} end={to === '/'} className="nq-navitem">
          <Icon size={16} />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
